import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import GestureIcon from '@mui/icons-material/Gesture';
import FilterListIcon from '@mui/icons-material/FilterList';
import SignatureCapture from './SignatureCapture';
import { fetchSignedDocument, listFormSubmissions, signFormSubmission } from '../../services/formsApi';

const STATUS_OPTIONS = ['all', 'pending', 'signed', 'overdue', 'cancelled'];

const STATUS_COLORS = {
  pending: 'warning',
  overdue: 'error',
  signed: 'success',
  cancelled: 'default'
};

const ActiveFormsPanel = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [signatureDialog, setSignatureDialog] = useState({ open: false, submission: null });
  const [signatureBusy, setSignatureBusy] = useState(false);
  const [signatureError, setSignatureError] = useState(null);
  const signaturePadRef = React.useRef(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listFormSubmissions();
      setSubmissions(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((submission) => {
      const matchesStatus =
        statusFilter === 'all' ? true : submission.status === statusFilter;
      const matchesSearch =
        searchTerm.trim().length === 0
          ? true
          : [
              submission.assigned_to,
              submission.metadata?.memo,
              submission.id,
              submission.template_id
            ]
              .filter(Boolean)
              .some((value) => value.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesStatus && matchesSearch;
    });
  }, [submissions, statusFilter, searchTerm]);

  const stats = useMemo(() => {
    return submissions.reduce(
      (acc, submission) => {
        acc.total += 1;
        acc[submission.status] = (acc[submission.status] || 0) + 1;
        return acc;
      },
      { total: 0 }
    );
  }, [submissions]);

  const openSignatureDialog = (submission) => {
    setSignatureDialog({ open: true, submission });
    setSignatureBusy(false);
    setSignatureError(null);
    signaturePadRef.current?.clear();
  };

  const closeSignatureDialog = () => {
    setSignatureDialog({ open: false, submission: null });
    setSignatureBusy(false);
    setSignatureError(null);
    signaturePadRef.current?.clear();
  };

  const handleSaveSignature = async () => {
    const submission = signatureDialog.submission;
    if (!submission) return;
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      setSignatureError('Capture a signature before saving.');
      return;
    }
    setSignatureBusy(true);
    setSignatureError(null);
    try {
      const signatureData = signaturePadRef.current.toDataURL('image/png');
      await signFormSubmission(submission.id, {
        signature_hash: signatureData,
        metadata: {
          signed_from: 'active-forms-panel'
        }
      });
      closeSignatureDialog();
      await loadData();
    } catch (err) {
      console.error(err);
      setSignatureError(err.message || 'Failed to capture signature');
    } finally {
      setSignatureBusy(false);
    }
  };

  const handleDownload = async (submission) => {
    try {
      const result = await fetchSignedDocument(submission.id);
      if (result.status !== 200 || !result.data?.signedUrl) {
        const message = result.data?.error || 'Signed document is not yet available.';
        alert(message);
        return;
      }
      window.open(result.data.signedUrl, '_blank', 'noopener');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Unable to download document.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(41,55,85,0.12)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              forms in circulation
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Chip label={`Pending ${stats.pending || 0}`} color="warning" variant="outlined" />
            <Chip label={`Signed ${stats.signed || 0}`} color="success" variant="outlined" />
            <Chip label={`Overdue ${stats.overdue || 0}`} color="error" variant="outlined" />
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <TextField
              label="Filter"
              select
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ mr: 1 }} fontSize="small" />
              }}
              sx={{ minWidth: 140 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Search"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="recipient, template, id"
            />
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(41,55,85,0.12)' }}>
        {filteredSubmissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {submissions.length === 0
              ? 'No active forms in circulation.'
              : 'No forms match your filters.'}
          </Typography>
        ) : isSmallScreen ? (
          <Stack spacing={2}>
            {filteredSubmissions.map((submission) => (
              <Paper key={submission.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {submission.assigned_to || 'Unknown recipient'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {submission.id.slice(0, 8)}…
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={submission.status.toUpperCase()}
                      color={STATUS_COLORS[submission.status] || 'default'}
                    />
                    {submission.due_date && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Due ${new Date(submission.due_date).toLocaleDateString()}`}
                      />
                    )}
                    {submission.submitted_at && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Signed ${new Date(submission.submitted_at).toLocaleString()}`}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Template ID: {submission.template_id}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GestureIcon fontSize="small" />}
                      onClick={() => openSignatureDialog(submission)}
                      disabled={submission.status !== 'pending'}
                    >
                      Capture
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={() => handleDownload(submission)}
                      disabled={submission.status !== 'signed'}
                    >
                      Download
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Due Date</TableCell>
                  <TableCell align="center">Submitted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubmissions.map((submission) => (
                  <TableRow key={submission.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {submission.assigned_to}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {submission.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>{submission.template_id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={submission.status.toUpperCase()}
                        color={STATUS_COLORS[submission.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {submission.due_date
                        ? new Date(submission.due_date).toLocaleDateString()
                        : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {submission.submitted_at
                        ? new Date(submission.submitted_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Capture signature">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={submission.status === 'signed'}
                              onClick={() => openSignatureDialog(submission)}
                            >
                              <GestureIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Download document">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              disabled={submission.status !== 'signed'}
                              onClick={() => handleDownload(submission)}
                            >
                              <DownloadIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={signatureDialog.open}
        onClose={closeSignatureDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Capture Signature</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recipient: {signatureDialog.submission?.assigned_to}
          </Typography>
          <SignatureCapture ref={signaturePadRef} height={220} />
          {signatureError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {signatureError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSignatureDialog} disabled={signatureBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<GestureIcon />}
            onClick={handleSaveSignature}
            disabled={signatureBusy}
          >
            {signatureBusy ? 'Saving…' : 'Save Signature'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ActiveFormsPanel;
